import React from "react";

import RecommendationWhyBadge from "./RecommendationWhyBadge";
import SectionHeading from "./SectionHeading";
import {Badge} from "./ui/badge";
import {Skeleton} from "./ui/skeleton";
import {cn} from "lib/utils";

/**
 * A personalised rail.
 *
 * Every NSL surface on the front page shares this presentation so the reader
 * can compare them fairly: same card, same measure, same picture ratio. What
 * differs between rails is the ranking, which is exactly what the demo is
 * meant to show — if the rails looked different, you could not tell whether a
 * change came from the engine or the layout.
 *
 * Cards are newsprint, not posters: the picture sits above the headline
 * instead of under it, so headlines stay black-on-paper and readable at a
 * glance. The ranking's own commentary — the why badge, the match figure —
 * is set in blue pencil, visually separate from the paper's voice.
 */

const RAIL_ITEM = "w-[280px] shrink-0 snap-start sm:w-[320px]";

const StoryRail = ({
  kicker,
  title,
  standfirst,
  tone,
  debugNote,
  debugMode,
  items = [],
  isLoading = false,
  onSelect,
  getItemRef,
  emptyTitle = "Nothing ranked yet",
  emptyBody = "Open a story or two and this rail will fill with what the engine learns.",
  action,
  className,
}) => (
  <section className={cn("container py-8", className)}>
    <SectionHeading
      kicker={kicker}
      title={title}
      standfirst={standfirst}
      tone={tone}
      debugNote={debugNote}
      debugMode={debugMode}
      action={action}
    />

    {isLoading ? (
      <div className="flex gap-6 overflow-hidden">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={cn(RAIL_ITEM, "flex flex-col gap-3")}>
            <Skeleton className="aspect-[3/2] w-full" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/4" />
          </div>
        ))}
      </div>
    ) : items.length ? (
      <div
        className={cn(
          "-mx-4 flex snap-x snap-mandatory gap-6 overflow-x-auto px-4 pb-2",
          "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        )}
      >
        {items.map((item) => (
          <article
            key={item.id}
            ref={getItemRef?.(item.id)}
            role="link"
            tabIndex={0}
            onClick={() => onSelect?.(item)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect?.(item);
              }
            }}
            className={cn(
              RAIL_ITEM,
              "group flex cursor-pointer flex-col gap-3",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-paper"
            )}
          >
            <div className="photo-inset relative aspect-[3/2] overflow-hidden rounded-sm bg-surface-2">
              {item.image ? (
                <img
                  src={item.image}
                  alt=""
                  loading="lazy"
                  className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                />
              ) : null}
              <RecommendationWhyBadge
                explanation={item.explanation}
                source={item.source}
                className="absolute left-2 top-2"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              {item.category ? (
                <Badge variant="kicker">{item.category}</Badge>
              ) : null}

              <h3 className="font-serif text-h3 font-bold leading-tight text-ink decoration-masthead/40 underline-offset-[3px] line-clamp-3 group-hover:underline">
                {item.title}
              </h3>

              {item.summary ? (
                <p className="text-body text-ink-secondary line-clamp-2">
                  {item.summary}
                </p>
              ) : null}

              <div className="mt-0.5 flex items-center gap-2">
                {item.meta ? (
                  <span className="font-sans text-meta text-ink-muted">
                    {item.meta}
                  </span>
                ) : null}
                {item.matchPercentage ? (
                  <span className="font-sans text-meta font-semibold text-pencil">
                    {item.matchPercentage}% match
                  </span>
                ) : null}
              </div>
            </div>
          </article>
        ))}
      </div>
    ) : (
      <div className="border border-dashed border-rule-strong px-6 py-12 text-center">
        <p className="font-serif text-h3 font-bold text-ink">{emptyTitle}</p>
        <p className="mx-auto mt-1.5 max-w-md text-body text-ink-tertiary">
          {emptyBody}
        </p>
      </div>
    )}
  </section>
);

export default StoryRail;
